import React, { useEffect, useCallback, useRef, useState, useMemo } from "react";
import { Sprite, Graphics, Container } from "@pixi/react";
import * as PIXI from "pixi.js";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { SOUND_NAME } from "../../../../sounds";
import { useReelControl } from "../../../../Contexts/ReelControlContext";
import { useReelRefs } from "../../../../Contexts/ReelRefsContext";
import { FEATURES_CONFIG } from "../../../../featuresConfig";
import {
  closeSymbolPopup,
  setSkipWinAnimation,
  setUserWantsToDismissWinAnimation,
  setAutoplaySpeed,
  closeMenu,
} from "../../../../State/actions";
import {
  selectAnyPopupActive,
  selectSessionExpired,
  selectIsSpinning,
  selectAutoPlayActive,
  selectHoldAndWinActive,
  selectLastCommand,
  selectFeatureState,
  selectFlowState,
  selectWindupComplete,
  selectErrorPopupActive,
  selectInfoPopupActive,
  selectPotCollectionActive,
  selectPotCollectionTriggered,
  selectPotCollectionTransitionActive,
} from "../../../../State/selectors";
import store from "../../../../State/Store/store";
import {
  createPlayButtonRotationAnimation,
  attemptFullscreenToggle,
} from "../../../../utilities";
import anime from "animejs";
import { getNewUITexture } from "../../../../assets";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { PixiSettings } from "../../../../pixiSettings";
import { soundManager } from "../../../../SoundManager";
import { isTurboLike } from "../../../../utils/speedHelpers";

/** True from bonus detect → cloud midpoint (ButtonPlay still mounted). */
const isPotCollectionEntryLocked = (state) =>
  !!(
    state.potCollection?.potCollectionTriggered ||
    state.animations?.potCollectionTransitionActive
  );

/**
 * Closes the one window in a replay where the button must not answer.
 *
 * ── NOT `sessionActive` ──
 *
 * That flag is true while a replay round is ACTIVELY PLAYING, and the player
 * drives each step with this very button. Locking on it stops the replay from
 * running at all. It is the wrong flag and must never be added here.
 *
 * The real gap is at the END. When a replayed bonus finishes, the base game is
 * back on screen and `openReplayEnded()` is fired from a 750ms TickerTimeout
 * (ReelControlContext). For those 750ms the round is over, no popup is open
 * yet, and nothing blocked the button — so spacebar started a round behind the
 * replay, and that press left the button stuck once the modal was dismissed.
 *
 * `replayFinished` is exactly that window: set by the spin result that ends the
 * replay, cleared when the next session starts.
 *
 * The two modal flags are kept as well. `selectAnyPopupActive` already covers
 * them in-game via `replayActive`, but the STANDALONE replay link never opens
 * the History board, so that cover does not exist there.
 */
const isReplayLocked = (state) =>
  !!(
    state.replay?.replayFinished ||
    state.userInterface?.replayEnterActive ||
    state.userInterface?.replayEndedActive
  );

// Play button configuration - matches UI button dimensions
const PLAY_BUTTON_CONFIG = {
  BASE_WIDTH: 170,
  BASE_HEIGHT: 170,
  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE_SMALL]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.9,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.45,
      },
    },
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 2.175,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.375,
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 2.25,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.675,
      },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      deviceMultiplier: 1.0,
    },
  },
};

// Input state tracking for spacebar and touch interactions
let spacePressed = false;
let spaceStartTime = 0;
let holdTimer = null;
let lastActionTime = 0;
const MIN_ACTION_GAP = 50; // Minimum gap between actions (ms) — 20 actions/sec max

// Touch state variables mirroring spacebar logic
let touchPressed = false;
let touchStartTime = 0;
let touchHoldTimer = null;
let touchId = null;

// Store subscription for counter-wait turbo queueing (mirrors spacebar auto-repeat)
let counterWaitUnsub = null;

// EVENT-LEVEL THROTTLE: Blocks click handler execution at entry point
// Prevents performance degradation from rapid clicking (50+ clicks/sec)
let clickThrottleActive = false;
let clickThrottleTimeout = null;

const ButtonPlay = ({ toolbar }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [actionInProgress, setActionInProgress] = useState(false);
  const [stopButtonDisabled, setStopButtonDisabled] = useState(false);

  const { playButtonRef } = useReelRefs();

  // Brightness filter for hover effect (secondary theme pattern)
  const brightnessFilter = useRef(null);
  if (!brightnessFilter.current) {
    brightnessFilter.current = new PIXI.ColorMatrixFilter();
    brightnessFilter.current.brightness(1.6, false);
    // Match filter framebuffer to device pixel ratio — prevents jagged edges on Retina/HiDPI
    brightnessFilter.current.resolution = window.devicePixelRatio || 1;
    brightnessFilter.current.padding = 8; // Large padding prevents edge clipping on curved sprites
  }
  const { dimensions, uiSymbolSize, device, orientation, getResponsiveConfig, isMobileSmall } =
    useDimensions();

  // Get responsive configuration from context - memoized to prevent recalculation
  const buttonConfig = useMemo(
    () => getResponsiveConfig(PLAY_BUTTON_CONFIG, true),
    [getResponsiveConfig]
  );

  // Refs for latest state access in event handlers
  const setActionInProgressRef = useRef(setActionInProgress);
  const setStopButtonDisabledRef = useRef(setStopButtonDisabled);

  const {
    handleSpin,
    handleManualStop,
    startSpacebarTurbo,
    stopSpacebarTurbo,
    spacebarTurboActive,
    pendingTensionRef,
    turboTensionEjectedRef,
  } = useReelControl();


  // Redux state selectors
  const sessionExpired = useSelector(selectSessionExpired);
  const isSpinning = useSelector(selectIsSpinning);
  const windupComplete = useSelector(selectWindupComplete);

  const autoPlayActive = useSelector(selectAutoPlayActive);

  const { winAnimationsPlaying, criticalAnimationPlaying } = useSelector(
    (state) => state.animations
  );
  const holdAndWinActive = useSelector(selectHoldAndWinActive);
  const lastCommand = useSelector(selectLastCommand);
  const featureState = useSelector(selectFeatureState);
  const flowState = useSelector(selectFlowState); // Legacy fallback

  const anyPopupActive = useSelector(selectAnyPopupActive);

  const errorPopupActive = useSelector(selectErrorPopupActive);
  const infoPopupActive = useSelector(selectInfoPopupActive);
  const potCollectionActive = useSelector(selectPotCollectionActive);
  const potCollectionTriggered = useSelector(selectPotCollectionTriggered);
  const potCollectionTransitionActive = useSelector(
    selectPotCollectionTransitionActive,
  );
  // Keep ButtonPlay inert until PC button swaps in at transition midpoint
  const pcEntryLock =
    potCollectionTriggered || potCollectionTransitionActive;

  // Refs for latest values
  const spacebarTurboActiveRef = useRef(spacebarTurboActive);
  const stopSpacebarTurboRef = useRef(stopSpacebarTurbo);
  const startSpacebarTurboRef = useRef(startSpacebarTurbo);
  const handleSpinRef = useRef(handleSpin);
  const handleManualStopRef = useRef(handleManualStop);

  const actionInProgressRef = useRef(actionInProgress);
  const stopButtonDisabledRef = useRef(stopButtonDisabled);
  const isSpinningRef = useRef(isSpinning);
  const windupCompleteRef = useRef(windupComplete);
  const attemptFullscreenToggleRef = useRef(attemptFullscreenToggle);

  // Action gating to prevent rapid clicks
  // Controls which action type is currently allowed: 'SPIN', 'STOP', or null (no action)
  const allowedActionRef = useRef(null);

  // Pending stop mechanism - allows user to queue a stop during windup
  // User can click SPIN then immediately STOP, but stop waits for windup to complete
  const pendingStopRef = useRef(false);

  // Direct store subscription ref for pending stop — fires synchronously on state change,
  // bypassing React's render cycle for ~32ms faster response
  const pendingStopUnsubRef = useRef(null);

  // Update refs when values change
  useEffect(() => {
    spacebarTurboActiveRef.current = spacebarTurboActive;
    stopSpacebarTurboRef.current = stopSpacebarTurbo;
    startSpacebarTurboRef.current = startSpacebarTurbo;
    handleSpinRef.current = handleSpin;
    handleManualStopRef.current = handleManualStop;

    actionInProgressRef.current = actionInProgress;
    stopButtonDisabledRef.current = stopButtonDisabled;
    isSpinningRef.current = isSpinning;
    windupCompleteRef.current = windupComplete;
    setActionInProgressRef.current = setActionInProgress;
    setStopButtonDisabledRef.current = setStopButtonDisabled;
  }, [
    spacebarTurboActive,
    stopSpacebarTurbo,
    startSpacebarTurbo,
    handleSpin,
    handleManualStop,

    actionInProgress,
    stopButtonDisabled,
    setActionInProgress,
    setStopButtonDisabled,
    isSpinning,
    windupComplete,
  ]);

  // Update function refs separately (these are imported functions, not state)
  useEffect(() => {
    attemptFullscreenToggleRef.current = attemptFullscreenToggle;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // attemptFullscreenToggle is a stable import

  // Button state

  // Initialize action gate on mount
  useEffect(() => {
    // On mount, if not spinning, allow spin
    if (!isSpinning) {
      allowedActionRef.current = 'SPIN';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount - isSpinning checked but not in deps intentionally

  // Action gate management - controls which action is allowed at each phase
  // Prevents rapid clicks by blocking actions that don't match the current game phase
  useEffect(() => {
    // When windup completes and we're spinning, allow STOP action
    if (isSpinning && windupComplete) {
      allowedActionRef.current = 'STOP';
      // NOTE: pending stop is handled by direct store subscription (pendingStopUnsubRef)
      // for near-instant execution — no React render cycle delay
    }
  }, [isSpinning, windupComplete]);

  // Helper: queue a pending stop with direct store subscription for instant execution
  const queuePendingStop = useCallback(() => {
    if (pendingStopRef.current) return; // Already queued
    pendingStopRef.current = true;

    // Clean up any existing subscription
    if (pendingStopUnsubRef.current) {
      pendingStopUnsubRef.current();
      pendingStopUnsubRef.current = null;
    }

    // Subscribe directly to Redux store — fires synchronously on dispatch,
    // no React render cycle needed
    pendingStopUnsubRef.current = store.subscribe(() => {
      const s = store.getState();
      if (s.spin.windupComplete && s.spin.isSpinning) {
        // Unsubscribe immediately to prevent double-fire
        if (pendingStopUnsubRef.current) {
          pendingStopUnsubRef.current();
          pendingStopUnsubRef.current = null;
        }
        pendingStopRef.current = false;
        allowedActionRef.current = null; // Lock immediately
        handleManualStopRef.current(); // Execute the queued stop
      }
    });
  }, []);

  // When spin completes, allow SPIN action for next spin (ONLY in manual mode, NOT autoplay)
  useEffect(() => {
    if (!isSpinning) {
      if (!autoPlayActive) {
        // Manual mode: Allow SPIN for next spin
        allowedActionRef.current = 'SPIN';
        pendingStopRef.current = false; // Clear any pending stop
      } else {
        // Autoplay mode: Keep gate NULL, spins are triggered automatically by ReelControlContext
        allowedActionRef.current = null;
        pendingStopRef.current = false; // Clear any pending stop
      }
      // Clean up pending stop subscription when spin ends
      if (pendingStopUnsubRef.current) {
        pendingStopUnsubRef.current();
        pendingStopUnsubRef.current = null;
      }
    }
  }, [isSpinning, autoPlayActive]);

  // Manage button enabled/disabled state based on game phase
  // Button must stay disabled during windup for ALL spins (manual + autoplay)
  // This prevents clicking before reels are actually spinning, which causes infinite spin bug
  useEffect(() => {
    // When spin completes, reset actionInProgress and re-enable button for next action
    if (!isSpinning && actionInProgress) {
      setActionInProgress(false);
      setStopButtonDisabled(false);
      return;
    }

    // Wait for windup to complete before allowing stop
    // Button re-enables when: spin is active AND windup is complete
    // If actionInProgress is true, it means we already queued/executed a stop, so keep it disabled
    if (isSpinning && windupComplete && !actionInProgress) {
      setActionInProgress(false);
      setStopButtonDisabled(false);
      return;
    }
  }, [actionInProgress, isSpinning, autoPlayActive, windupComplete]);

  // ONLY reset button state and stop turbo for BIG WIN animations
  // Counter animations and expanding wilds should NOT reset button state
  // This allows turbo to resume after counter finishes if button still held
  useEffect(() => {
    if (winAnimationsPlaying) {
      // Clean up touch state completely for big win animations
      if (touchHoldTimer) {
        clearTimeout(touchHoldTimer);
        touchHoldTimer = null;
      }
      if (counterWaitUnsub) {
        counterWaitUnsub();
        counterWaitUnsub = null;
      }
      touchPressed = false;
      touchStartTime = 0;
      touchId = null;

      // Clean up spacebar state completely for big win animations
      if (holdTimer) {
        clearTimeout(holdTimer);
        holdTimer = null;
      }
      spacePressed = false;
      spaceStartTime = 0;

      // Lift the spacebar hold for win animations.
      // stopSpacebarTurbo restores previousSpeedRef which now correctly
      // preserves the turbo button's speed setting (turbo/super_turbo).
      if (spacebarTurboActive) {
        stopSpacebarTurboRef.current();
      }
    }
  }, [winAnimationsPlaying, spacebarTurboActive, dispatch]);

  // CRITICAL: Clear hold state when critical animation starts (Hold and Win detection ONLY)
  // This prevents button release from triggering a spin when Hold and Win is detected
  // User might be holding button when Hold and Win triggers, so we clear hold state immediately
  // NOTE: Expanding wilds also set criticalAnimationPlaying, but we DON'T clear hold state for those
  // (they should work like regular wins - turbo continues after counter finishes)
  useEffect(() => {
    if (criticalAnimationPlaying) {
      // Check if this is for Hold and Win (not expanding wilds)
      // Use universal featureState if available, fall back to legacy flowState
      const isHoldAndWinTrigger =
        FEATURES_CONFIG.HOLD_AND_WIN.ENABLED &&
        ((featureState && featureState.type === "holdAndWin" && featureState.phase === "intro") ||
         (lastCommand === "spin" && flowState === "respin"));

      // Only clear hold state for Hold and Win, NOT for expanding wilds
      if (isHoldAndWinTrigger || holdAndWinActive) {
        // Clean up touch state completely when Hold and Win starts
        if (touchHoldTimer) {
          clearTimeout(touchHoldTimer);
          touchHoldTimer = null;
        }
        if (counterWaitUnsub) {
          counterWaitUnsub();
          counterWaitUnsub = null;
        }
        touchPressed = false;
        touchStartTime = 0;
        touchId = null;

        // Clean up spacebar state completely when Hold and Win starts
        if (holdTimer) {
          clearTimeout(holdTimer);
          holdTimer = null;
        }
        spacePressed = false;
        spaceStartTime = 0;

        // Stop turbo if active when Hold and Win starts
        if (spacebarTurboActive) {
          const currentState = store.getState();
          if (isTurboLike(currentState.settings.autoPlaySpeed)) {
            dispatch(setAutoplaySpeed("auto"));
          }
          stopSpacebarTurboRef.current();
        }
      }
      // If it's expanding wilds (criticalAnimationPlaying but NOT Hold and Win),
      // do nothing - let turbo continue naturally after counter finishes
    }
  }, [
    criticalAnimationPlaying,
    spacebarTurboActive,
    dispatch,
    holdAndWinActive,
    featureState,
    lastCommand,
    flowState,
  ]);

  // Safety Effect: Stop turbo and clear hold state when error/info popups appear
  // Prevents turbo spins from continuing when critical popups are displayed (e.g., free rounds ending)
  // Similar to how turbo stops for win animations, this ensures users can't bypass important messages
  const previousPopupFlagsRef = useRef({
    errorPopupActive: false,
    infoPopupActive: false,
  });

  useEffect(() => {
    const errorPopupJustOpened =
      errorPopupActive && !previousPopupFlagsRef.current.errorPopupActive;
    const infoPopupJustOpened =
      infoPopupActive && !previousPopupFlagsRef.current.infoPopupActive;

    // Stop turbo and clear hold state if any popup just opened
    if ((errorPopupJustOpened || infoPopupJustOpened) && spacebarTurboActive) {
      // Clean up touch state completely for popups
      if (touchHoldTimer) {
        clearTimeout(touchHoldTimer);
        touchHoldTimer = null;
      }
      if (counterWaitUnsub) {
        counterWaitUnsub();
        counterWaitUnsub = null;
      }
      touchPressed = false;
      touchStartTime = 0;
      touchId = null;

      // Clean up spacebar state completely for popups
      if (holdTimer) {
        clearTimeout(holdTimer);
        holdTimer = null;
      }
      spacePressed = false;
      spaceStartTime = 0;

      // Stop turbo completely for popups
      if (spacebarTurboActive) {
        const currentState = store.getState();
        if (isTurboLike(currentState.settings.autoPlaySpeed)) {
          dispatch(setAutoplaySpeed("auto"));
        }
        stopSpacebarTurboRef.current();
      }
    }

    previousPopupFlagsRef.current = {
      errorPopupActive,
      infoPopupActive,
    };
  }, [errorPopupActive, infoPopupActive, spacebarTurboActive, dispatch]);

  // ============================================================================
  // CENTRALIZED ACTION RESOLUTION SYSTEM
  // ============================================================================

  // Determine if turbo mode should be allowed to START.
  // Only blocks during big win popup (winAnimationsPlaying).
  // Counter display (small wins) is NOT blocked — the turbo Continuous Effect
  // in ReelControlContext natively waits for counterAnimationFinished before
  // firing the next spin. This allows click/tap to queue turbo during the
  // unskippable win animation, matching spacebar's continuous-hold behavior.
  const shouldAllowTurbo = useCallback(() => {
    const state = store.getState();
    return !state.animations.winAnimationsPlaying;
  }, []);

  // Resolve what action the button should perform
  const resolveButtonAction = useCallback(() => {
    const state = store.getState();
    const autoPlayActive = state.settings.autoPlayActive;
    const autoPlaySpeed = state.settings.autoPlaySpeed;

    // Priority 1: Skip animations if they're active
    if (state.animations.winAnimationsPlaying) {
      return {
        type: "SKIP_ANIMATIONS",
        winAnimationsPlaying: state.animations.winAnimationsPlaying,
        isWaitingForConfirmation: state.animations.isWaitingForUserConfirmation,
      };
    }

    // Priority 2: Block ALL actions if autoplay is active AND speed is turbo
    if (autoPlayActive && isTurboLike(autoPlaySpeed)) {
      return {
        type: "NO_OP",
      };
    }

    // Priority 3: Stop spin if spinning
    // CRITICAL: During autoplay, if manual stop is already pending, don't allow another stop
    if (state.spin.isSpinning) {
      if (autoPlayActive && state.spin.manualStop) {
        // Manual stop already triggered, don't allow another one
        return {
          type: "NO_OP",
        };
      }
      return {
        type: "STOP_SPIN",
        autoPlayActive: autoPlayActive,
        autoPlaySpeed: autoPlaySpeed,
      };
    }

    // Priority 4: Block starting new spin if autoplay is active
    if (autoPlayActive) {
      return {
        type: "NO_OP",
      };
    }

    // Priority 5: Start spin
    return {
      type: "START_SPIN",
    };
  }, []);

  // Execute skip animations action
  const executeSkipAnimations = useCallback(
    (actionData) => {
      soundManager.play(SOUND_NAME.button_click);

      if (actionData.winAnimationsPlaying) {
        if (actionData.isWaitingForConfirmation) {
          dispatch(setUserWantsToDismissWinAnimation(true));
        } else {
          dispatch(setSkipWinAnimation(true));
        }
      }
    },
    [dispatch]
  );

  // Execute stop spin action
  const executeStopSpin = useCallback((actionData) => {
    // Regular reel stop - check turbo restrictions
    // Don't allow manual stop in regular turbo mode (non-autoplay)
    if (!actionData.autoPlayActive && isTurboLike(actionData.autoPlaySpeed)) {
      return false;
    }

    // Don't allow manual stop in autoplay turbo mode (already blocked in resolveButtonAction, but double-check)
    if (actionData.autoPlayActive && isTurboLike(actionData.autoPlaySpeed)) {
      return false;
    }

    // Block ALL actions until spin completes
    allowedActionRef.current = null;

    soundManager.play(SOUND_NAME.button_click);
    lastActionTime = Date.now();
    setActionInProgressRef.current(true);
    setStopButtonDisabledRef.current(true);
    handleManualStopRef.current();

    return true;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Execute start spin action
  const executeStartSpin = useCallback((source = "click") => {
    const state = store.getState();

    // Block manual spin if autoplay is active
    // Autoplay manages its own spin cycle - manual spins should not interfere
    if (state.settings.autoPlayActive) {
      return false;
    }

    // Block ALL actions during windup phase
    allowedActionRef.current = null;

    // Trigger animations on the play button
    if (playButtonRef.current) {
      // Click/touch: gentle scale pulse + rotation combined
      // Spacebar: rotation only
      if (source !== "spacebar") {
        const sprite = playButtonRef.current;
        anime.remove(sprite.scale);
        const origX = sprite.scale.x;
        const origY = sprite.scale.y;
        anime({
          targets: sprite.scale,
          x: origX * 0.93,
          y: origY * 0.93,
          duration: 142,
          easing: "easeOutQuad",
          complete: () => {
            anime({
              targets: sprite.scale,
              x: origX,
              y: origY,
              duration: 180,
              easing: "easeOutQuad",
            });
          },
        });
      }
      createPlayButtonRotationAnimation(playButtonRef.current);
    }

    // Allow spin regardless of balance - API will handle insufficient credits
    soundManager.play(SOUND_NAME.spin);
    lastActionTime = Date.now();
    actionInProgressRef.current = true;
    handleSpinRef.current();

    return true;
  }, [playButtonRef]);



  // Master action executor
  const executeButtonAction = useCallback(
    (action, source = "click") => {
      // Visual feedback already handled at top of handleClick and handleKeyDown
      // No need to add it here anymore

      switch (action.type) {
        case "SKIP_ANIMATIONS":
          executeSkipAnimations(action);
          break;
        case "STOP_SPIN":
          executeStopSpin(action);
          break;
        case "START_SPIN":
          executeStartSpin(source);
          break;
        case "NO_OP":
          // Do nothing - autoplay is active, just ignore the click
          break;
        default:
          // Unknown action type - ignore
          break;
      }
    },
    [executeSkipAnimations, executeStopSpin, executeStartSpin]
  );

  // ============================================================================
  // SIMPLIFIED EVENT HANDLERS
  // ============================================================================

  // Handle click function - now uses centralized action resolution
  const handleClick = useCallback(() => {
    // EVENT-LEVEL THROTTLE: FIRST LINE - Instant return if throttle is active
    // Prevents performance degradation from rapid clicking (50+ clicks/sec)
    if (clickThrottleActive) {
      return; // Instant exit - zero overhead, no logs, no code execution
    }

    // Set throttle flag IMMEDIATELY and schedule single reset
    clickThrottleActive = true;
    if (clickThrottleTimeout) {
      clearTimeout(clickThrottleTimeout);
    }
    clickThrottleTimeout = setTimeout(() => {
      clickThrottleActive = false;
      clickThrottleTimeout = null;
    }, MIN_ACTION_GAP);

    // EARLY TURBO CHECK: Skip all expensive work if turbo mode is active
    // This check happens before state reads, action resolution, and animations
    const quickState = store.getState();

    // During active tension, skip is handled by pointerDown/keyDown — exit silently
    // Prevents double-firing on desktop where both pointerdown and click fire
    if (pendingTensionRef?.current && quickState.spin.isSpinning) {

      return;
    }

    if (quickState.settings.autoPlayActive && isTurboLike(quickState.settings.autoPlaySpeed)) {

      return; // Exit immediately - no visual feedback, no expensive work
    }

    // Determine what action will be taken
    const action = resolveButtonAction();

    // Visual feedback is centralized in executeStartSpin (rotation) and executeStopSpin (press)

    // Close menu if open and attempt fullscreen toggle
    dispatch(closeMenu());
    attemptFullscreenToggleRef.current(dispatch, device);

    // Block clicks if there's a win that hasn't been fully displayed yet
    // Only applies when counterAnimationStarted is true — animated wins (Big Win, H&W trigger)
    // skip the counter so they must not be blocked by this check.
    const hasWin =
      quickState.game.currentRound?.win && quickState.game.currentRound.win > 0;
    const winNotFullyDisplayed =
      hasWin &&
      quickState.spin.spinComplete &&
      quickState.animations.counterAnimationStarted &&
      !quickState.animations.counterAnimationFinished;

    // Check for critical animation (expanding wilds, etc.)
    if (quickState.animations.criticalAnimationPlaying) {

      return; // Visual feedback already played
    }

    // PC intro→cloud: ButtonPlay stays mounted until midpoint — never spin here
    if (isPotCollectionEntryLocked(quickState) || isReplayLocked(quickState)) {
      return;
    }

    if (
      anyPopupActive ||
      sessionExpired ||
      quickState.userInterface.userInterfaceDisabled ||
      actionInProgressRef.current ||
      winNotFullyDisplayed
    ) {
      return; // Visual feedback already played
    }

    const now = Date.now();
    if (now - lastActionTime < MIN_ACTION_GAP) {

      return; // Visual feedback already played
    }

    // Action gating - block if action type doesn't match what's allowed
    if (action.type === 'START_SPIN' && allowedActionRef.current !== 'SPIN') {

      return;
    }
    if (action.type === 'STOP_SPIN') {
      if (allowedActionRef.current === 'STOP') {
        // Windup complete, execute stop immediately
      } else if (allowedActionRef.current === null && isSpinningRef.current && !windupCompleteRef.current) {
        // During windup, queue the stop (only once)
        if (!pendingStopRef.current) {
          queuePendingStop();

          // Disable button immediately when stop is queued
          setActionInProgressRef.current(true);
        }
        return; // Don't execute yet (visual feedback already played at top)
      } else {

        // Not allowed at all
        return;
      }
    }


    executeButtonAction(action, "click");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    anyPopupActive,
    sessionExpired,
    resolveButtonAction,
    executeButtonAction,
    playButtonRef,
    dispatch,
    device,
    buttonConfig.width,
    buttonConfig.height,
  ]);

  // Touch pointer down handler - handles turbo activation on hold
  const handlePointerDown = useCallback(
    (event) => {
      const state = store.getState();

      // Block touch while counter is showing (PopiWinDisplay displays for 2 seconds)
      const counterIsShowing =
        state.animations.counterAnimationStarted &&
        !state.animations.counterAnimationFinished;

      // Check for critical animation (expanding wilds, etc.)
      if (state.animations.criticalAnimationPlaying) {
        return;
      }

      if (isPotCollectionEntryLocked(state) || isReplayLocked(state)) {
        return;
      }

      // TENSION SKIP: During active tension, each tap/click skips the current tension reel.
      // Works in ALL speed modes — tension is identical across auto/quick/turbo.
      // NOTE: No turboTensionEjectedRef guard here — that only blocks held spacebar.
      // A click/tap is always a fresh intentional action.
      if (pendingTensionRef?.current && state.spin.isSpinning) {
        event.stopPropagation();
        event.preventDefault();
        soundManager.play(SOUND_NAME.button_click);
        handleManualStopRef.current();
        return;
      }

      if (
        touchPressed ||
        selectAnyPopupActive(state) ||
        state.game.sessionExpired ||
        state.userInterface.userInterfaceDisabled ||
        actionInProgressRef.current
      ) {
        return;
      }

      // During counter display: wait for counter to finish, then start turbo.
      // This mirrors spacebar behavior — keyboard auto-repeat keeps firing KEY_DOWN
      // events that are blocked during counter, and the first one after counter finishes
      // gets through and starts turbo. Button has no auto-repeat, so we use a direct
      // store subscription to achieve the same effect.
      if (counterIsShowing) {
        if (!spacebarTurboActiveRef.current && !counterWaitUnsub) {
          touchId = event.pointerId;
          touchPressed = true;
          touchStartTime = Date.now();

          event.stopPropagation();
          event.preventDefault();

          // Direct store subscription — fires synchronously on dispatch,
          // bypasses React batching (same pattern as pendingStopRef)
          counterWaitUnsub = store.subscribe(() => {
            const s = store.getState();
            const counterDone =
              !s.animations.counterAnimationStarted ||
              s.animations.counterAnimationFinished;

            if (!touchPressed) {
              // User released before counter finished — clean up
              if (counterWaitUnsub) {
                counterWaitUnsub();
                counterWaitUnsub = null;
              }
              return;
            }

            if (counterDone) {
              // Unsubscribe immediately to prevent double-fire
              if (counterWaitUnsub) {
                counterWaitUnsub();
                counterWaitUnsub = null;
              }

              // Now start the hold timer — same as the normal (non-counter) path
              if (!spacebarTurboActiveRef.current && shouldAllowTurbo()) {
                const currentState = store.getState();
                if (currentState.settings.autoPlayActive) {
                  touchPressed = false;
                  touchStartTime = 0;
                  touchId = null;
                  return;
                }
                if (
                  !currentState.spin.isSpinning &&
                  !currentState.settings.autoPlayActive
                ) {
                  lastActionTime = Date.now();
                  startSpacebarTurboRef.current();
                }
              }
            }
          });
        }
        return; // Block all actions — counter-wait subscription handles turbo
      }

      const now = Date.now();
      if (now - lastActionTime < MIN_ACTION_GAP) return;

      event.stopPropagation();
      event.preventDefault();

      // Block ALL actions if autoplay is active AND speed is turbo
      if (
        state.settings.autoPlayActive &&
        isTurboLike(state.settings.autoPlaySpeed)
      ) {
        return;
      }

      // Handle autoplay: Simple one-action rule
      // If spinning, trigger manual stop ONCE, then clear hold state
      if (state.settings.autoPlayActive) {
        // If already pressed, ignore (prevents duplicate stops)
        if (touchPressed) {
          return;
        }

        // Mark as pressed to prevent duplicates
        touchPressed = true;
        touchStartTime = now;
        touchId = event.pointerId;

        // If spinning, trigger manual stop once
        if (state.spin.isSpinning && !state.spin.manualStop) {
          const action = resolveButtonAction();

          // Action gating for touch with pending stop support
          if (action.type === "STOP_SPIN") {
            if (allowedActionRef.current === 'STOP') {
              // Windup complete, execute stop immediately
              executeButtonAction(action, "click");
            } else if (allowedActionRef.current === null && state.spin.isSpinning && !state.spin.windupComplete) {
              // During windup, queue the stop (only once)
              if (!pendingStopRef.current) {
                queuePendingStop();

                // Disable button immediately when stop is queued
                setActionInProgressRef.current(true);
              }
            }
          }
        }

        // Clear hold timer (no turbo during autoplay)
        if (touchHoldTimer) {
          clearTimeout(touchHoldTimer);
          touchHoldTimer = null;
        }

        // Don't set up turbo timer - autoplay doesn't allow turbo
        return;
      }

      // Stop turbo if already active (from spacebar)
      if (spacebarTurboActiveRef.current) {
        stopSpacebarTurboRef.current();
        return;
      }

      // Setup touch tracking (needed for both tap and hold detection)
      touchId = event.pointerId;
      touchPressed = true;
      touchStartTime = Date.now();

      // Only start turbo timer if animations are NOT active
      if (shouldAllowTurbo()) {
        // Start turbo after hold
        touchHoldTimer = setTimeout(() => {
          if (touchPressed && !spacebarTurboActiveRef.current) {
            // Double-check animations haven't started
            if (!shouldAllowTurbo()) {
              return;
            }

            const currentState = store.getState();

            // CRITICAL: Block turbo activation during autoplay
            if (currentState.settings.autoPlayActive) {
              touchPressed = false;
              touchStartTime = 0;
              touchId = null;
              return;
            }

            if (
              !currentState.spin.isSpinning &&
              !currentState.settings.autoPlayActive
            ) {
              // Allow turbo regardless of balance - API will handle insufficient credits
              lastActionTime = Date.now();
              startSpacebarTurboRef.current();

            }
          }
        }, FEATURES_CONFIG.TURBO.SPIN_SENSITIVITY);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [resolveButtonAction, executeButtonAction, shouldAllowTurbo, playButtonRef, buttonConfig.width, buttonConfig.height]
  );

  // Touch pointer up handler - stops turbo or performs quick tap action
  const handlePointerUp = useCallback(
    (event) => {
      if (!touchPressed || event.pointerId !== touchId) return;

      event.stopPropagation();
      event.preventDefault();

      const state = store.getState();

      // Handle autoplay: On release, just reset state - NO ACTIONS
      // Manual stop should ONLY happen on pointerDown, not on pointerUp
      // This prevents double-stop issues that cause infinite spin bugs
      if (state.settings.autoPlayActive) {
        // Clear timer and reset state
        if (touchHoldTimer) {
          clearTimeout(touchHoldTimer);
          touchHoldTimer = null;
        }
        touchPressed = false;
        touchStartTime = 0;
        touchId = null;

        return; // No turbo during autoplay, no actions on release
      }

      // Block clicks if there's a win that hasn't been fully displayed yet
      // Only applies when counterAnimationStarted is true — animated wins skip the counter.
      const hasWin =
        state.game.currentRound?.win && state.game.currentRound.win > 0;
      const winNotFullyDisplayed =
        hasWin &&
        state.spin.spinComplete &&
        state.animations.counterAnimationStarted &&
        !state.animations.counterAnimationFinished;

      const holdDuration = Date.now() - touchStartTime;

      // Clear timer and reset state immediately to prevent stuck state
      if (touchHoldTimer) {
        clearTimeout(touchHoldTimer);
        touchHoldTimer = null;
      }
      touchPressed = false;
      touchStartTime = 0;
      touchId = null;

      // Clear turbo tension eject flag on touch release
      if (turboTensionEjectedRef?.current) {
        turboTensionEjectedRef.current = false;
      }

      if (actionInProgressRef.current) return;

      // If critical animation is playing AND turbo is active, stop turbo (button released during critical animation)
      if (
        state.animations.criticalAnimationPlaying &&
        spacebarTurboActiveRef.current
      ) {
        lastActionTime = Date.now();
        stopSpacebarTurboRef.current();
        return;
      }

      // Block other actions if critical animation is playing
      if (state.animations.criticalAnimationPlaying) {
        return;
      }

      if (isPotCollectionEntryLocked(state) || isReplayLocked(state)) {
        return;
      }

      // If win is being displayed AND turbo is active, stop turbo (button released during win display)
      if (winNotFullyDisplayed && spacebarTurboActiveRef.current) {
        lastActionTime = Date.now();
        stopSpacebarTurboRef.current();
        return;
      }

      // Block other actions if win is being displayed
      if (winNotFullyDisplayed) {
        return;
      }

      // Stop turbo if active
      if (spacebarTurboActiveRef.current) {
        lastActionTime = Date.now();
        stopSpacebarTurboRef.current();
      } else if (holdDuration < FEATURES_CONFIG.TURBO.SPIN_SENSITIVITY) {
        // Quick tap - resolve and execute action
        const action = resolveButtonAction();

        // Action gating for touch
        if (action.type === 'START_SPIN' && allowedActionRef.current !== 'SPIN') {
          return;
        }
        if (action.type === 'STOP_SPIN' && allowedActionRef.current !== 'STOP') {
          return;
        }

        executeButtonAction(action, "click");
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [resolveButtonAction, executeButtonAction]
  );

  // Touch pointer cancel handler - cleanup on touch interruption
  const handlePointerCancel = useCallback((event) => {
    if (event.pointerId !== touchId) return;

    event.stopPropagation();
    event.preventDefault();

    // Clear timer and stop turbo if active
    if (touchHoldTimer) {
      clearTimeout(touchHoldTimer);
      touchHoldTimer = null;
    }
    if (counterWaitUnsub) {
      counterWaitUnsub();
      counterWaitUnsub = null;
    }
    if (spacebarTurboActiveRef.current) {
      stopSpacebarTurboRef.current();
    }

    // Reset touch state
    touchPressed = false;
    touchStartTime = 0;
    touchId = null;
  }, []);

  // Calculate responsive button position based on device and orientation - memoized
  // Uses toolbar width instead of full screen width
  const buttonPosition = useMemo(() => {
    if (isMobileSmall) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: toolbar.width / 2,
          y: -toolbar.visualHeight - buttonConfig.height / 2 - buttonConfig.height * 0.075,
        };
      } else {
        return {
          x: toolbar.width - buttonConfig.width * 0.55,
          y: -dimensions.height / 2 + buttonConfig.height / 1.8,
        };
      }
    } else if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: toolbar.width / 2,
          y: -toolbar.visualHeight - buttonConfig.height / 2 - buttonConfig.height * 0.065,
        };
      } else {
        // landscape
        return {
          x: toolbar.width - buttonConfig.width * 0.415,
          y: -dimensions.height / 2 + buttonConfig.height / 1.8,
        };
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: toolbar.width / 2,
          y: -dimensions.height / 5.0,
        };
      } else {
        // landscape
        return {
          x: toolbar.width - buttonConfig.width / 1.4,
          y: toolbar.height - buttonConfig.height / 1.7,
        };
      }
    } else {
      const yOffset = 0.08;
      return {
        x: toolbar.width - buttonConfig.width * 1.285,
        y: uiSymbolSize * yOffset,
      };
    }
  }, [
    device,
    orientation,
    isMobileSmall,
    dimensions.height,
    toolbar.width,
    toolbar.height,
    toolbar.visualHeight,
    buttonConfig.width,
    buttonConfig.height,
    uiSymbolSize,
  ]);

  // Get the appropriate icon texture - always use play icon
  const iconTexture = getNewUITexture("icon_play");

  // Hover: brightness filter on sprite (secondary theme pattern)
  const handlePointerOver = useCallback(() => {
    const sprite = playButtonRef.current;
    if (sprite && (!sprite.filters || sprite.filters.length === 0)) {
      sprite.filters = [brightnessFilter.current];
    }
  }, [playButtonRef]);

  const handlePointerOut = useCallback(() => {
    // Remove brightness filter from sprite
    const sprite = playButtonRef.current;
    if (sprite) sprite.filters = [];

    // CRITICAL: Trigger liftoff if pointer leaves while button is held
    // This handles the edge case where user holds button then drags off it
    if (touchPressed) {
      // Clear timer and reset state immediately to prevent stuck state
      if (touchHoldTimer) {
        clearTimeout(touchHoldTimer);
        touchHoldTimer = null;
      }
      if (counterWaitUnsub) {
        counterWaitUnsub();
        counterWaitUnsub = null;
      }

      // Reset touch state
      touchPressed = false;
      touchStartTime = 0;
      touchId = null;

      // FORCEFULLY stop turbo if active (user dragged finger off button while turbo was running)
      if (spacebarTurboActiveRef.current) {
        lastActionTime = Date.now();
        stopSpacebarTurboRef.current();
      }
    }
  }, [playButtonRef]);


  // Prevent context menu on long press/touch-and-hold
  useEffect(() => {
    const preventContextMenu = (event) => {
      event.preventDefault();
      return false;
    };

    document.addEventListener("contextmenu", preventContextMenu, {
      passive: false,
    });
    return () => {
      document.removeEventListener("contextmenu", preventContextMenu);


    };
  }, []);

  // Keyboard event handling - spacebar turbo activation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.code !== "Space" && event.key !== " " && event.keyCode !== 32) {
        return;
      }

      // Block spacebar during PotCollection bonus — PC button handles it
      if (potCollectionActive) {
        return;
      }

      event.preventDefault();

      // EVENT-LEVEL THROTTLE: Instant return if throttle is active
      // Prevents performance degradation from rapid spacebar spam
      if (clickThrottleActive) {
        return; // Instant exit - zero overhead
      }

      // Set throttle flag IMMEDIATELY and schedule single reset
      clickThrottleActive = true;
      if (clickThrottleTimeout) {
        clearTimeout(clickThrottleTimeout);
      }
      clickThrottleTimeout = setTimeout(() => {
        clickThrottleActive = false;
        clickThrottleTimeout = null;
      }, MIN_ACTION_GAP);

      const state = store.getState();

      // Close menu if open (matches click behavior)
      dispatch(closeMenu());

      if (state.userInterface.symbolPopupActive) {
        dispatch(closeSymbolPopup());
      }

      const updatedState = store.getState();

      // Determine what action will be taken
      const action = resolveButtonAction();

      // Visual feedback is action-driven:
      // - START_SPIN → rotation animation (triggered by handleSpin in ReelControlContext)
      // - STOP_SPIN → press animation (triggered by executeStopSpin when action executes)

      // Block spacebar while counter is showing (PopiWinDisplay displays for 2 seconds)
      const counterIsShowing =
        updatedState.animations.counterAnimationStarted &&
        !updatedState.animations.counterAnimationFinished;

      // TENSION SKIP: During active tension, each spacebar press skips the current tension reel.
      // NOTE: turboTensionEjectedRef blocks held spacebar from re-triggering after tension eject.
      if (pendingTensionRef?.current && updatedState.spin.isSpinning) {
        if (!turboTensionEjectedRef?.current) {
          soundManager.play(SOUND_NAME.button_click);
          handleManualStopRef.current();
          // If turbo is active, mark as ejected so held spacebar doesn't re-skip
          if (spacebarTurboActiveRef.current) {
            turboTensionEjectedRef.current = true;
          }
        }
        return;
      }

      // Check for critical animation (expanding wilds, etc.)
      if (updatedState.animations.criticalAnimationPlaying) {
        return;
      }

      if (isPotCollectionEntryLocked(updatedState) || isReplayLocked(updatedState)) {
        return;
      }

      if (
        selectAnyPopupActive(updatedState) ||
        updatedState.game.sessionExpired ||
        updatedState.userInterface.userInterfaceDisabled ||
        actionInProgressRef.current ||
        counterIsShowing
      ) {
        return;
      }

      // Ignore key repeat events (if spacebar is already pressed, ignore)
      if (spacePressed && event.repeat) {
        return;
      }

      const now = Date.now();
      if (now - lastActionTime < MIN_ACTION_GAP) return;

      // Block ALL actions if autoplay is active AND speed is turbo
      if (
        updatedState.settings.autoPlayActive &&
        isTurboLike(updatedState.settings.autoPlaySpeed)
      ) {
        return;
      }

      // Handle autoplay: Simple one-action rule
      // If spinning, trigger manual stop ONCE, then clear hold state
      if (updatedState.settings.autoPlayActive) {
        // If already pressed, ignore (prevents duplicate stops from key repeat)
        if (spacePressed) {
          return;
        }

        // Mark as pressed to prevent duplicates
        spacePressed = true;
        spaceStartTime = now;

        // If spinning, trigger manual stop once
        if (updatedState.spin.isSpinning && !updatedState.spin.manualStop) {
          // Action gating for spacebar with pending stop support
          if (action.type === "STOP_SPIN") {
            if (allowedActionRef.current === 'STOP') {
              // Windup complete, execute stop immediately
              executeButtonAction(action, "spacebar");
            } else if (allowedActionRef.current === null && updatedState.spin.isSpinning && !updatedState.spin.windupComplete) {
              // During windup, queue the stop (only once)
              if (!pendingStopRef.current) {
                queuePendingStop();

                // Disable button immediately when stop is queued
                setActionInProgressRef.current(true);
              }
            }
          }
        }

        // Clear hold timer (no turbo during autoplay)
        if (holdTimer) {
          clearTimeout(holdTimer);
          holdTimer = null;
        }

        // Don't set up turbo timer - autoplay doesn't allow turbo
        return;
      }

      // Start turbo after hold (but not during animations)
      if (!spacePressed) {
        // Setup spacebar tracking (needed for both tap and hold detection)
        spacePressed = true;
        spaceStartTime = now;

        // Only start turbo timer if animations are NOT active
        if (shouldAllowTurbo()) {
          holdTimer = setTimeout(() => {
            if (spacePressed) {
              // Double-check animations haven't started
              if (!shouldAllowTurbo()) {
                return;
              }

              const currentState = store.getState();

              // CRITICAL: Block turbo activation during autoplay
              if (currentState.settings.autoPlayActive) {
                spacePressed = false;
                spaceStartTime = 0;
                return;
              }

              if (
                !currentState.spin.isSpinning &&
                !currentState.settings.autoPlayActive
              ) {
                // Allow turbo regardless of balance - API will handle insufficient credits
                lastActionTime = Date.now();
                startSpacebarTurboRef.current();

              }
            }
          }, FEATURES_CONFIG.TURBO.SPIN_SENSITIVITY);
        }
      }
    };

    const handleKeyUp = (event) => {
      if (event.code !== "Space" && event.key !== " " && event.keyCode !== 32) {
        return;
      }

      // Block spacebar during PotCollection bonus — PC button handles it
      if (potCollectionActive) {
        return;
      }



      if (!spacePressed) return;

      const state = store.getState();

      // Handle autoplay: On release, just reset state - NO ACTIONS
      // Manual stop should ONLY happen on keyDown, not on keyUp
      // This prevents double-stop issues that cause infinite spin bugs
      if (state.settings.autoPlayActive) {
        // Clear timer and reset state
        if (holdTimer) {
          clearTimeout(holdTimer);
          holdTimer = null;
        }
        spacePressed = false;
        spaceStartTime = 0;

        return; // No turbo during autoplay, no actions on release
      }

      // Block clicks if there's a win that hasn't been fully displayed yet
      // Only applies when counterAnimationStarted is true — animated wins skip the counter.
      const hasWin =
        state.game.currentRound?.win && state.game.currentRound.win > 0;
      const winNotFullyDisplayed =
        hasWin &&
        state.spin.spinComplete &&
        state.animations.counterAnimationStarted &&
        !state.animations.counterAnimationFinished;

      const holdDuration = Date.now() - spaceStartTime;

      // Clear timer and reset state immediately to prevent stuck state
      if (holdTimer) {
        clearTimeout(holdTimer);
        holdTimer = null;
      }
      spacePressed = false;
      spaceStartTime = 0;

      // Clear turbo tension eject flag on spacebar release so a fresh press can skip tension
      if (turboTensionEjectedRef?.current) {
        turboTensionEjectedRef.current = false;
      }

      if (actionInProgressRef.current) {
        return;
      }

      // If critical animation is playing AND turbo is active, stop turbo (spacebar released during critical animation)
      if (
        state.animations.criticalAnimationPlaying &&
        spacebarTurboActiveRef.current
      ) {
        lastActionTime = Date.now();
        stopSpacebarTurboRef.current();
        return;
      }

      // Block other actions if critical animation is playing
      if (state.animations.criticalAnimationPlaying) {
        return;
      }

      if (isPotCollectionEntryLocked(state) || isReplayLocked(state)) {
        return;
      }

      // If win is being displayed AND turbo is active, stop turbo (button released during win display)
      if (winNotFullyDisplayed && spacebarTurboActiveRef.current) {
        lastActionTime = Date.now();
        stopSpacebarTurboRef.current();
        return;
      }

      // Block other actions if win is being displayed
      if (winNotFullyDisplayed) {
        return;
      }

      // Stop turbo if active, otherwise perform quick tap action
      if (spacebarTurboActiveRef.current) {
        lastActionTime = Date.now();
        stopSpacebarTurboRef.current();
      } else if (holdDuration < FEATURES_CONFIG.TURBO.SPIN_SENSITIVITY) {
        // Quick tap - resolve and execute action
        const action = resolveButtonAction();

        // Action gating for spacebar with pending stop support
        if (action.type === 'START_SPIN' && allowedActionRef.current !== 'SPIN') {
          return;
        }
        if (action.type === 'STOP_SPIN') {
          if (allowedActionRef.current === 'STOP') {
            // Windup complete, execute stop immediately
          } else if (allowedActionRef.current === null && isSpinningRef.current && !windupCompleteRef.current) {
            // During windup, queue the stop (only once)
            if (!pendingStopRef.current) {
              queuePendingStop();

              // Disable button immediately when stop is queued
              setActionInProgressRef.current(true);
            }
            return; // Don't execute yet
          } else {
            // Not allowed at all
            return;
          }
        }

        executeButtonAction(action, "spacebar");
      }
    };

    const handleWindowBlur = () => {
      // Cleanup on window blur
      if (holdTimer) {
        clearTimeout(holdTimer);
        holdTimer = null;
      }
      if (spacebarTurboActiveRef.current) {
        stopSpacebarTurboRef.current();
      }
      spacePressed = false;
      spaceStartTime = 0;
    };

    // Safety net: visibilitychange fires on tab switch, mobile app switch,
    // and dev tools open — cases where window blur may not fire,
    // preventing turbo from getting stuck without a held spacebar.
    const handleVisibilityChange = () => {
      if (document.hidden) {
        handleWindowBlur();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", handleWindowBlur);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      // Cleanup: clear all timers, stop turbo, reset state
      if (holdTimer) clearTimeout(holdTimer);
      if (touchHoldTimer) clearTimeout(touchHoldTimer);
      if (counterWaitUnsub) {
        counterWaitUnsub();
        counterWaitUnsub = null;
      }
      if (spacebarTurboActiveRef.current) {
        stopSpacebarTurboRef.current();
      }

      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", handleWindowBlur);
      document.removeEventListener("visibilitychange", handleVisibilityChange);

      spacePressed = false;
      spaceStartTime = 0;

      // Cleanup throttle timeout
      if (clickThrottleTimeout) {
        clearTimeout(clickThrottleTimeout);
        clickThrottleTimeout = null;
        clickThrottleActive = false;
      }
      holdTimer = null;
      touchPressed = false;
      touchStartTime = 0;
      touchHoldTimer = null;
      touchId = null;

      // Clean up pending stop subscription
      if (pendingStopUnsubRef.current) {
        pendingStopUnsubRef.current();
        pendingStopUnsubRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    resolveButtonAction,
    executeButtonAction,
    shouldAllowTurbo,
    playButtonRef,
    potCollectionActive,
    buttonConfig.width,
    buttonConfig.height,
  ]);

  // Calculate button radius to match the visual sprite exactly - memoized
  const buttonRadius = useMemo(() => buttonConfig.width / 2, [buttonConfig.width]);

  return (
    <Container name="secondary_button_play" x={buttonPosition.x} y={buttonPosition.y}>
        {/* Interactive circular area - handles all pointer events */}
        <Graphics
          name="button_play_interactive_area"
          eventMode={pcEntryLock ? "none" : "static"}
          cursor={pcEntryLock ? "default" : "pointer"}
          hitArea={new PIXI.Circle(0, 0, buttonRadius)}
          onpointerover={handlePointerOver}
          onpointerout={handlePointerOut}
          onpointerdown={handlePointerDown}
          onpointerup={handlePointerUp}
          onpointercancel={handlePointerCancel}
          onpointertap={handleClick}
          draw={(g) => {
            g.clear();
            // Nearly invisible fill (required for hit detection to work)
            g.beginFill(0x000000, 0.01);
            g.drawCircle(0, 0, buttonRadius);
            g.endFill();
          }}
          accessible={true}
          accessibleTitle={
            isSpinning
              ? t("stop_button", { ns: "common" })
              : t("play_button", { ns: "common" })
          }
        />

        {/* Visual sprite (non-interactive, just for display) */}
        <Sprite
          ref={playButtonRef}
          texture={iconTexture}
          name="button_play_icon"
          anchor={0.5}
          width={buttonConfig.width}
          height={buttonConfig.height}
          roundPixels={true}
          x={0}
          y={0}
          eventMode="none"
          alpha={1}
        />
    </Container>
  );
};

export default ButtonPlay;
